import { useEffect, useMemo, useRef } from "react";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import { SlicerTokenInput } from "../shared/SlicerTokenInput";
import {
  Button,
  Collapse,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Typography,
} from "antd";
import { DeleteOutlined, PlusOutlined, SwapOutlined } from "@ant-design/icons";
import { useReduxMetricDropdown } from "../shared/useReduxMetricDropdown";
import { MetricSelect } from "../shared/MetricSelect";
import {
  CountIncludePicker,
  countSourceFromMetricId,
  defaultCountSpec,
} from "../shared/CountIncludePicker";
import type {
  Table2DComponentSpec,
  Table2DDataSpec,
  Table2DDimension,
  MetricOption,
  SubjectEntity,
} from "../../Processing/types";
import { Table2DHeatmap } from "../../WidgetComponents/Table2DHeatmap";
import { AGG_OPTIONS } from "../constants";
import { ConfigPanelLayout } from "../shared/ConfigPanelLayout";
import { PreviewShell } from "../shared/PreviewShell";
import { PreviewDisclaimer } from "../shared/PreviewDisclaimer";
import { DateRangeForm } from "../shared/DateRangeForm";
import { resolveDateRange } from "../../Utils/resolveDateRange";
import { PlayersForm } from "../shared/PlayersForm";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import { ToggleOption } from "../shared/ToggleOption";
import type { Table2DMetricEntry } from "./table2dState";
import { isSectionBindable } from "../../Processing/controllerBindings";
import { SectionSummary } from "../shared/SectionSummary";
import {
  table2dReducer,
  createInitialState,
  hydrateFromSpec,
  formStateToSpec,
  ALL_DIM_OPTIONS,
  validColOptions,
  withCountDimRules,
  isPinnedRelevant,
  pinnedAreRows,
} from "./table2dState";

const { Panel } = Collapse;
const { Text } = Typography;


// ── Mock preview data ──

const MOCK_ENTITY_LABELS: Record<string, string[]> = {
  player:   ["Player 1", "Player 2", "Player 3", "Player 4", "Player 5", "Player 6", "Player 7", "Player 8", "Player 9", "Player 10"],
  position: ["Goalkeeper", "Defender", "Midfielder", "Winger", "Forward", "Striker", "Fullback", "Center Back", "Sweeper", "Wingback"],
  drill:    ["Drill A", "Drill B", "Drill C", "Drill D", "Drill E", "Drill F", "Drill G", "Drill H", "Drill I", "Drill J"],
};

// Match the backend label formats: day = YYYY-MM-DD, week = ISO Monday date
// (YYYY-MM-DD), month = YYYY-MM (see DashboardEngine._extract_query_result).
const MOCK_TIME_LABELS: Record<string, string[]> = {
  day:   ["2025-04-14", "2025-04-15", "2025-04-16", "2025-04-17", "2025-04-18", "2025-04-19", "2025-04-20", "2025-04-21", "2025-04-22", "2025-04-23"],
  week:  ["2025-01-06", "2025-01-13", "2025-01-20", "2025-01-27", "2025-02-03", "2025-02-10", "2025-02-17", "2025-02-24", "2025-03-03", "2025-03-10"],
  month: ["2025-01", "2025-02", "2025-03", "2025-04", "2025-05", "2025-06", "2025-07", "2025-08", "2025-09", "2025-10"],
  weekday: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
};

const MOCK_METRIC_LABELS = ["Distance", "Sprints", "Top Speed", "Load", "ACWR", "HSR", "Accels", "Decels", "Player Load", "Impacts"];

// Preview matrix dimensions: 6 rows × 8 columns.
const PREVIEW_ROWS = 6;
const PREVIEW_COLS = 8;

function mockDimLabels(dim: Table2DDimension, metricEntries: Table2DMetricEntry[], metricDropdown: any[]): string[] {
  if (dim === "metric") {
    return metricEntries.map((m, i) => {
      if (m.metricId) {
        for (const g of metricDropdown) {
          const opt = g.options?.find((o: any) => o.value === m.metricId);
          if (opt) return opt.displayLabel ?? opt.label;
        }
      }
      return MOCK_METRIC_LABELS[i] ?? `Metric ${i + 1}`;
    });
  }
  return MOCK_ENTITY_LABELS[dim] ?? MOCK_TIME_LABELS[dim] ?? ["—"];
}

function mockValue(rowIdx: number, colIdx: number): number {
  return Math.round(100 + ((rowIdx * 37 + colIdx * 19 + rowIdx * colIdx * 7) % 101));
}

// Synthesize a red→orange→yellow→green hex for the preview heatmap (real colors
// come from the backend; here we fake a gradient so the builder preview is faithful).
const MOCK_HEX_STOPS: [number, [number, number, number]][] = [
  [0, [217, 83, 79]], [0.3, [239, 118, 23]], [0.55, [255, 200, 61]], [1, [49, 180, 82]],
];

function mockHex(value: number, min: number, max: number): string {
  const t = max > min ? (value - min) / (max - min) : 0.5;
  let lo = MOCK_HEX_STOPS[0];
  let hi = MOCK_HEX_STOPS[MOCK_HEX_STOPS.length - 1];
  for (let i = 0; i < MOCK_HEX_STOPS.length - 1; i++) {
    if (t >= MOCK_HEX_STOPS[i][0] && t <= MOCK_HEX_STOPS[i + 1][0]) {
      lo = MOCK_HEX_STOPS[i];
      hi = MOCK_HEX_STOPS[i + 1];
      break;
    }
  }
  const span = hi[0] - lo[0] || 1;
  const f = (t - lo[0]) / span;
  const ch = (a: number, b: number) => Math.round(a + (b - a) * f).toString(16).padStart(2, "0");
  return `#${ch(lo[1][0], hi[1][0])}${ch(lo[1][1], hi[1][1])}${ch(lo[1][2], hi[1][2])}`;
}

// ── Metric row form (one entry in the metrics list) ──

interface MetricRowProps {
  entry: Table2DMetricEntry;
  index: number;
  showLabel: string;
  metricDropdown: any[];
  canRemove: boolean;
  onChange: (patch: Partial<Omit<Table2DMetricEntry, "id">>) => void;
  onRemove: () => void;
}

function MetricRow({ entry, index: _index, showLabel, metricDropdown, canRemove, onChange, onRemove }: MetricRowProps) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "8px 10px",
        border: "1px solid #e0e5eb",
        borderRadius: 8,
        background: "#fafbfc",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Text style={{ fontSize: 11, color: "#888", minWidth: 18 }}>{showLabel}</Text>
        <MetricSelect
          metricDropdown={metricDropdown}
          value={entry.metricId}
          onChange={(v) => {
            const source = countSourceFromMetricId(v);
            onChange({
              metricId: v,
              // One source owns the include-list, so a metric change replaces it.
              countSpec: source ? defaultCountSpec(source) : undefined,
              // Forced for a count; restored when leaving one.
              ...(source
                ? { metricAgg: "sum" as const }
                : entry.countSpec ? { metricAgg: "avg" as const } : {}),
            });
          }}
          style={{ flex: 1 }}
        />
        {canRemove && (
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
            style={{ flexShrink: 0 }}
          />
        )}
      </div>
      {entry.countSpec && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 24 }}>
          <Text style={{ fontSize: 11, color: "#888" }}>Counting</Text>
          <CountIncludePicker
            spec={entry.countSpec}
            onChange={(countSpec) => onChange({ countSpec })}
          />
        </div>
      )}
      {entry.metricId != null && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 24 }}>
          <Text style={{ fontSize: 11, color: "#888" }}>Agg</Text>
          {entry.countSpec ? (
            <span className="awm-count-fixed-agg">Total</span>
          ) : (
            <Segmented
              size="small"
              options={AGG_OPTIONS.map((a) => ({ label: a.label, value: a.value }))}
              value={entry.metricAgg}
              onChange={(v) => onChange({ metricAgg: v as "avg" | "sum" | "best" })}
            />
          )}
        </div>
      )}
    </div>
  );
}


// ── Main configurator ──


export function Table2DConfigurator({
  onSpecChange,
  existingSpec,
  metrics,
  getSubjectEntities,
}: ConfiguratorSharedProps<Table2DComponentSpec>) {
  const [state, dispatch] = useConfiguratorSync({
    reducer: table2dReducer,
    existingSpec,
    hydrate: hydrateFromSpec,
    createInitial: createInitialState,
    toSpec: formStateToSpec,
    onSpecChange,
    metrics,
    getSubjectEntities,
  });

  // ── Redux metric dropdown ──
  const { metricDropdown } = useReduxMetricDropdown({ includeCounts: true });

  // For a brand-new table, default to the first metric in the list once the
  // dropdown loads. Runs once and skips editing so a user's choice (or a
  // deliberately cleared metric) is never overwritten.
  const didAutoSelectMetric = useRef(false);
  useEffect(() => {
    if (didAutoSelectMetric.current || existingSpec) return;
    if (state.metrics[0]?.metricId != null) return;
    const firstMetric = metricDropdown[0]?.options?.[0]?.value;
    if (firstMetric == null) return;
    didAutoSelectMetric.current = true;
    dispatch({ type: "UPDATE_METRIC", index: 0, patch: { metricId: firstMetric } });
  }, [existingSpec, metricDropdown, state.metrics, dispatch]);

  // ── Derived state ──
  // The backend rejects a drill group-by on a count, so the Drill dimension is closed
  // off while one is selected.
  const hasCountMetric = state.metrics.some((m) => m.countSpec);
  const isMetricDimension = state.rowDimension === "metric" || state.colDimension === "metric";
  const showPinned = isPinnedRelevant(state.rowDimension, state.colDimension);
  const pinnedAsRows = showPinned && pinnedAreRows(state.rowDimension, state.colDimension);

  const metricPanelSummary = useMemo(() => {
    const configured = state.metrics.filter((m) => m.metricId != null);
    if (configured.length === 0) return null;
    if (configured.length === 1) {
      for (const g of metricDropdown) {
        const opt = g.options?.find((o: any) => o.value === configured[0].metricId);
        if (opt) return opt.displayLabel ?? opt.label;
      }
    }
    return `${configured.length} metrics`;
  }, [state.metrics, metricDropdown]);

  // ── Preview: synthesize a mock Table2DDataSpec so the real heatmap component renders ──
  const previewHeatmap = useMemo(() => {
    const rowLabels = mockDimLabels(state.rowDimension, state.metrics, metricDropdown).slice(0, PREVIEW_ROWS);
    const colLabels = mockDimLabels(state.colDimension, state.metrics, metricDropdown).slice(0, PREVIEW_COLS);

    const matrix = rowLabels.map((_, r) => colLabels.map((_, c) => mockValue(r, c)));
    const allVals = matrix.flat();
    const min = Math.min(...allVals);
    const max = Math.max(...allVals);

    const cells: Table2DDataSpec["cells"] = [];
    rowLabels.forEach((rk, r) =>
      colLabels.forEach((ck, c) => {
        cells.push({ row_key: rk, col_key: ck, value: matrix[r][c], hex: mockHex(matrix[r][c], min, max) });
      }),
    );

    // Pinned values are keyed by the ENTITY axis (rows when entity-on-rows, else cols).
    const entityIsRow = !pinnedAsRows;
    const best: Record<string, number> = {};
    const avg: Record<string, number> = {};
    const total: Record<string, number> = {};
    if (showPinned) {
      if (entityIsRow) {
        rowLabels.forEach((rk, r) => {
          const vals = matrix[r];
          best[rk] = Math.max(...vals);
          avg[rk] = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
          total[rk] = vals.reduce((a, b) => a + b, 0);
        });
      } else {
        colLabels.forEach((ck, c) => {
          const vals = rowLabels.map((_, r) => matrix[r][c]);
          best[ck] = Math.max(...vals);
          avg[ck] = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
          total[ck] = vals.reduce((a, b) => a + b, 0);
        });
      }
    }

    const table2d: Table2DDataSpec = {
      row_keys: rowLabels,
      row_labels: Object.fromEntries(rowLabels.map((l) => [l, l])),
      col_keys: colLabels,
      col_labels: Object.fromEntries(colLabels.map((l) => [l, l])),
      cells,
      pinned_best: showPinned && state.showBestCol ? best : null,
      pinned_avg: showPinned && state.showAvgCol ? avg : null,
      pinned_total: showPinned && state.showTotalCol ? total : null,
    };

    const spec: Table2DComponentSpec = {
      type: "table_2d",
      display: { title: state.title || "" },
      rowDimension: state.rowDimension,
      colDimension: state.colDimension,
      queryIds: [],
      colorize: true,
      showDataLabels: state.showDataLabels,
      pinnedCols: {
        best: showPinned && state.showBestCol,
        avg: showPinned && state.showAvgCol,
        total: showPinned && state.showTotalCol,
      },
      table_2d_builder_misc: {},
    };

    return { table2d, spec };
  }, [
    state.rowDimension,
    state.colDimension,
    state.metrics,
    state.title,
    state.showBestCol,
    state.showAvgCol,
    state.showTotalCol,
    state.showDataLabels,
    showPinned,
    pinnedAsRows,
    metricDropdown,
  ]);

  // ── Plain table preview data (used when Heatmap Mode is off) ──
  const tablePreviewData = useMemo(() => {
    const baseRowLabels = mockDimLabels(state.rowDimension, state.metrics, metricDropdown).slice(0, PREVIEW_ROWS);
    const baseColLabels = mockDimLabels(state.colDimension, state.metrics, metricDropdown).slice(0, PREVIEW_COLS);
    const nRows = baseRowLabels.length;
    const nCols = baseColLabels.length;

    const antdCols: any[] = [
      { key: "__row", title: "", dataIndex: "__row", fixed: "left" as const, width: 90,
        render: (v: string) => <Text style={{ fontSize: 11, fontWeight: 600 }}>{v}</Text> },
    ];

    if (!pinnedAsRows && showPinned) {
      if (state.showBestCol) antdCols.push({ key: "__best", title: "Best", dataIndex: "__best", width: 60, align: "right" as const });
      if (state.showAvgCol) antdCols.push({ key: "__avg", title: "Avg", dataIndex: "__avg", width: 60, align: "right" as const });
      if (state.showTotalCol) antdCols.push({ key: "__total", title: "Sum", dataIndex: "__total", width: 60, align: "right" as const });
    }
    for (let c = 0; c < nCols; c++) {
      antdCols.push({ key: String(c), title: baseColLabels[c], dataIndex: String(c), width: 60, align: "right" as const });
    }

    const rows: any[] = baseRowLabels.map((label, r) => {
      const row: Record<string, any> = { key: String(r), __row: label };
      for (let c = 0; c < nCols; c++) row[String(c)] = mockValue(r, c);
      if (!pinnedAsRows && showPinned) {
        const vals = Array.from({ length: nCols }, (_, c) => mockValue(r, c));
        if (state.showBestCol) row["__best"] = Math.max(...vals);
        if (state.showAvgCol) row["__avg"] = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
        if (state.showTotalCol) row["__total"] = vals.reduce((a, b) => a + b, 0);
      }
      return row;
    });

    if (pinnedAsRows && showPinned) {
      const pinnedRows: any[] = [];
      if (state.showBestCol) {
        const row: Record<string, any> = { key: "__best", __row: "Best" };
        for (let c = 0; c < nCols; c++) {
          const vals = Array.from({ length: nRows }, (_, r) => mockValue(r, c));
          row[String(c)] = Math.max(...vals);
        }
        pinnedRows.push(row);
      }
      if (state.showAvgCol) {
        const row: Record<string, any> = { key: "__avg", __row: "Avg" };
        for (let c = 0; c < nCols; c++) {
          const vals = Array.from({ length: nRows }, (_, r) => mockValue(r, c));
          row[String(c)] = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
        }
        pinnedRows.push(row);
      }
      if (state.showTotalCol) {
        const row: Record<string, any> = { key: "__total", __row: "Sum" };
        for (let c = 0; c < nCols; c++) {
          const vals = Array.from({ length: nRows }, (_, r) => mockValue(r, c));
          row[String(c)] = vals.reduce((a, b) => a + b, 0);
        }
        pinnedRows.push(row);
      }
      rows.unshift(...pinnedRows);
    }

    return { columns: antdCols, rows };
  }, [state.rowDimension, state.colDimension, state.metrics, state.showBestCol, state.showAvgCol, state.showTotalCol, showPinned, pinnedAsRows, metricDropdown]);

  // ── Summary labels ──
  const dateLabel = resolveDateRange(state);
  const totalSelected = state.selectedSubjects.length + state.selectedDrills.length;

  // ── Form ──
  const form = (
    <>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8ecf0" }}>
        <Space size={8} align="center" style={{ width: "100%" }}>
          <Text style={{ fontSize: 12, minWidth: 40 }}>Title</Text>
          <SlicerTokenInput
            size="small"
            placeholder="e.g. Sprint Load by Player"
            value={state.title}
            onChange={(title) => dispatch({ type: "SET_TITLE", title })}
            style={{ flex: 1 }}
          />
        </Space>
      </div>

      <Collapse
        accordion
        activeKey={state.expandedKey}
        onChange={(key) => dispatch({ type: "SET_EXPANDED_KEY", key })}
        style={{ borderRadius: 0, borderTop: "none" }}
      >
        <Panel
          key="__date_range"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">1</span>
              <Text strong style={{ fontSize: 13 }}>Date Range</Text>
              <SectionSummary kind="date" controllerId={state.dateControllerId} own={dateLabel} />
            </div>
          }
        >
          <DateRangeForm
            allowLatestN
            bindable={isSectionBindable("table_2d", "date")}
            value={state}
            onChange={(patch) => dispatch({ type: "UPDATE_DATE_RANGE", patch })}
          />
        </Panel>

        <Panel
          key="__players"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">2</span>
              <Text strong style={{ fontSize: 13 }}>Players</Text>
              <SectionSummary kind="subject" controllerId={state.subjectControllerId}
                own={totalSelected > 0 ? `${totalSelected} selected` : "Full Team"}
                getSubjectEntities={getSubjectEntities} />
            </div>
          }
        >
          <PlayersForm
            hideDrills={state.metrics.some((m) => m.countSpec)}
            bindable={isSectionBindable("table_2d", "subject")}
            value={state}
            onChange={(patch) => dispatch({ type: "UPDATE_PLAYERS", patch })}
            getSubjectEntities={getSubjectEntities}
          />
          <div
            style={{
              display: "flex", justifyContent: "space-between", alignItems: "flex-start",
              padding: "8px 10px", border: "1px solid #e0e5eb", borderRadius: 8, background: "#fafbfc",
              margin: "8px 0 0",
            }}
          >
            <div>
              <Text strong style={{ fontSize: 12 }}>Exclude Injured Players</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>
                Hide injured athletes' rows.
              </Text>
            </div>
            <Switch
              size="small"
              checked={state.excludeInjuredPlayers}
              onChange={(v) => dispatch({ type: "SET_EXCLUDE_INJURED_PLAYERS", value: v })}
            />
          </div>
        </Panel>

        <Panel
          key="__metrics"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">3</span>
              <Text strong style={{ fontSize: 13 }}>
                {isMetricDimension ? "Metrics" : "Metric"}
              </Text>
              {metricPanelSummary && (
                <Text type="secondary" style={{ fontSize: 11 }}>{metricPanelSummary}</Text>
              )}
            </div>
          }
        >
          <div className="awm-step-body">
            {isMetricDimension ? (
              // Multi-metric mode: one row per metric dimension entry
              <>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Each metric becomes a header on the{" "}
                  <strong>{state.rowDimension === "metric" ? "row" : "column"}</strong> axis.
                </Text>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {state.metrics.map((entry, i) => (
                    <MetricRow
                      key={entry.id}
                      entry={entry}
                      index={i}
                      showLabel={String(i + 1)}
                      metricDropdown={metricDropdown}
                      canRemove={state.metrics.length > 1}
                      onChange={(patch) => dispatch({ type: "UPDATE_METRIC", index: i, patch })}
                      onRemove={() => dispatch({ type: "REMOVE_METRIC", index: i })}
                    />
                  ))}
                </div>
                <Button
                  type="text"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => dispatch({ type: "ADD_METRIC" })}
                  style={{ fontWeight: 600, color: "#1677ff", paddingLeft: 0 }}
                >
                  Add Metric
                </Button>
              </>
            ) : (
              // Single-metric mode
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <MetricSelect
                  metricDropdown={metricDropdown}
                  value={state.metrics[0]?.metricId}
                  onChange={(v) => {
                    // The same rules MetricRow applies in multi-metric mode: one source
                    // owns the include-list, and a count is always a total.
                    const source = countSourceFromMetricId(v);
                    dispatch({
                      type: "UPDATE_METRIC",
                      index: 0,
                      patch: {
                        metricId: v,
                        countSpec: source ? defaultCountSpec(source) : undefined,
                        ...(source
                          ? { metricAgg: "sum" as const }
                          : state.metrics[0]?.countSpec ? { metricAgg: "avg" as const } : {}),
                      },
                    });
                  }}
                />
                {state.metrics[0]?.countSpec && (
                  <div className="awm-labeled-row">
                    <Text className="awm-labeled-row-label">Counting</Text>
                    <CountIncludePicker
                      spec={state.metrics[0].countSpec}
                      onChange={(countSpec) =>
                        dispatch({ type: "UPDATE_METRIC", index: 0, patch: { countSpec } })
                      }
                    />
                  </div>
                )}
                {state.metrics[0]?.metricId != null && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Text style={{ fontSize: 12 }}>Aggregation</Text>
                    {state.metrics[0].countSpec ? (
                      <span className="awm-count-fixed-agg">Total</span>
                    ) : (
                      <Segmented
                        size="small"
                        options={AGG_OPTIONS.map((a) => ({ label: a.label, value: a.value }))}
                        value={state.metrics[0].metricAgg}
                        onChange={(v) =>
                          dispatch({ type: "UPDATE_METRIC", index: 0, patch: { metricAgg: v as "avg" | "sum" | "best" } })
                        }
                      />
                    )}
                  </div>
                )}
              </Space>
            )}
          </div>
        </Panel>
      </Collapse>

      {/* Display options */}
      <div style={{ padding: "12px 16px", borderTop: "1px solid #e8ecf0", display: "flex", flexDirection: "column", gap: 8 }}>
        <ToggleOption
          label="Heatmap Mode"
          description="Render as a color-coded heatmap. When off, displays as a plain table."
          checked={state.colorize}
          onChange={(v) => dispatch({ type: "SET_COLORIZE", value: v })}
        />
        {state.colorize && (
          <ToggleOption
            label="Show Data Labels"
            description="Show the numeric value in each heatmap cell. Turn off for a compact, color-only view."
            checked={state.showDataLabels}
            onChange={(v) => dispatch({ type: "SET_SHOW_DATA_LABELS", value: v })}
          />
        )}
        {showPinned && (
          <>
            <ToggleOption
              label={pinnedAsRows ? 'Show "Best" Row' : 'Show "Best" Column'}
              description={
                pinnedAsRows
                  ? "Append a row with each column's all-time best value."
                  : "Prepend a column with each row's all-time best value."
              }
              checked={state.showBestCol}
              onChange={(v) => dispatch({ type: "SET_SHOW_BEST_COL", value: v })}
            />
            <ToggleOption
              label={pinnedAsRows ? 'Show "Avg" Row' : 'Show "Avg" Column'}
              description={
                pinnedAsRows
                  ? "Append a row with each column's historical average."
                  : "Prepend a column with each row's historical average."
              }
              checked={state.showAvgCol}
              onChange={(v) => dispatch({ type: "SET_SHOW_AVG_COL", value: v })}
            />
            <ToggleOption
              label={pinnedAsRows ? 'Show "Sum" Row' : 'Show "Sum" Column'}
              description={
                pinnedAsRows
                  ? "Append a row with each column's sum."
                  : "Prepend a column with each row's sum."
              }
              checked={state.showTotalCol}
              onChange={(v) => dispatch({ type: "SET_SHOW_TOTAL_COL", value: v })}
            />
          </>
        )}
      </div>
    </>
  );

  // ── Preview ──
  const preview = (
    <>
      <PreviewShell
        title={state.title || "2D Table Preview"}
        titleAddon={<PreviewDisclaimer />}
        hasContent={true}
        summaryText={
          isMetricDimension
            ? `${state.metrics.filter((m) => m.metricId).length} of ${state.metrics.length} metrics configured`
            : state.metrics[0]?.metricId
              ? `${metricPanelSummary ?? "Metric"} · ${state.metrics[0].metricAgg}`
              : "Select a metric to configure"
        }
      >
        {state.colorize ? (
          <Table2DHeatmap
            table2d={previewHeatmap.table2d}
            spec={previewHeatmap.spec}
            showDataLabels={state.showDataLabels}
          />
        ) : (
          <Table
            columns={tablePreviewData.columns}
            dataSource={tablePreviewData.rows}
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
            style={{ width: "100%" }}
          />
        )}
      </PreviewShell>

      {/* Dimension picker: rows and cols side by side; cols filtered by row choice */}
      <div
        className="awm-groupby-bar"
        style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-start", gap: 10 }}
      >
        <Text type="secondary" style={{ fontSize: 11 }}>Rows</Text>
        <Select
          size="small"
          value={state.rowDimension}
          options={withCountDimRules(ALL_DIM_OPTIONS, hasCountMetric)}
          onChange={(v: Table2DDimension) => dispatch({ type: "SET_ROW_DIMENSION", value: v })}
          style={{ width: 110 }}
          popupMatchSelectWidth={false}
        />
        <Text type="secondary" style={{ fontSize: 11 }}>Cols</Text>
        <Select
          size="small"
          value={state.colDimension}
          options={withCountDimRules(validColOptions(state.rowDimension), hasCountMetric)}
          onChange={(v: Table2DDimension) => dispatch({ type: "SET_COL_DIMENSION", value: v })}
          style={{ width: 110 }}
          popupMatchSelectWidth={false}
        />
        <Button
          size="small"
          icon={<SwapOutlined />}
          onClick={() => dispatch({ type: "SWAP_DIMENSIONS" })}
        >
          Swap
        </Button>
      </div>
    </>
  );

  return <ConfigPanelLayout form={form} preview={preview} />;
}
